/* eslint-disable react-hooks/exhaustive-deps */
import { QuestionCircleOutlined, SyncOutlined } from '@ant-design/icons'
import { Button, Col, DatePicker, Form, FormInstance, notification, Select, Tooltip } from 'antd/es'
import { useWatch } from 'antd/es/form/Form'
import TextArea from 'antd/es/input/TextArea'
import dayjs, { Dayjs } from 'dayjs'
import {
  Dispatch,
  FC,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'

import { QUERY_KEYS } from '@api'
import { NoteElement, TicketFilesVariantDetail, TicketFilesVariantForm } from '@components'
import { forbiddenSupplierEditionStatus } from '@constants'
import { useUser } from '@contexts'
import {
  DeliveryNoteLabel,
  useDestinations,
  useDeliveryNotes,
  useEquipment,
  useMaterials,
  usePurchaseOrdersByMaterial,
  useSuppliers,
} from '@hooks'
import {
  Note,
  RoleEnum,
  SapItemSupplier,
  StatusTicketEnum,
  Supplier,
  Ticket,
  TripTypeEnum,
} from '@types'
import {
  CutoffTimeUtils,
  DeliveryNoteUtils,
  filterPurchaseOrdersByTripType,
  OtherUtils,
  StringUtils,
} from '@utils'

import { CommentElement } from '../../../../components/CommentElement'
import StatusTicket from '../../../../components/StatusTicket'
import {
  FlexCol,
  FlexRow,
  MaterialLabelContainer,
  StyledInput,
  StyledRow,
} from '../../../../components/StyledComponents'
import { DATE_KEYS } from '../../../../constants/dateKeys'
import { mapDestinationsToCombo } from '../../../../utils/array-entity-to-combo'

const { trimText, classesToString } = StringUtils

const { PURCHASES } = QUERY_KEYS

const { PENDING, CONFIRMED, CREATED, TRANSIT, DELIVERED, CANCELED } = StatusTicketEnum
const declinedLoadEditionStatus = [CONFIRMED, TRANSIT, DELIVERED]
const destinationEditableStatuses = [PENDING, CREATED, CONFIRMED, TRANSIT]
const editablePurchaseOrderStatus = [CREATED, PENDING, CONFIRMED, TRANSIT]

const handleFilterOptions = (input: string, option: any) => {
  if (option.label) return option.label.toString().toLowerCase().includes(input.toLowerCase())
  return true
}

const today = dayjs()
const tomorrow = today.clone().add(1, 'day').startOf('day')
const required = true

type Dates = Date[] | undefined

interface TicketFormProps {
  form: FormInstance<any>
  handlePrimaryButtonDisabling: (value: boolean) => void
  editable: boolean | undefined
  setEditable: Dispatch<SetStateAction<boolean | undefined>>
  dates: Dates
  setDates: Dispatch<SetStateAction<Dates>>
  isWarning: boolean | undefined
  setIsWarning: Dispatch<SetStateAction<boolean | undefined>>
  ticket: Ticket
  maxAllowedDaysForSupplierRequest?: number
  cutoffTime: string
}
const UpdateTicketFormSupplier: FC<PropsWithChildren<TicketFormProps>> = ({
  handlePrimaryButtonDisabling,
  editable,
  setEditable,
  ticket,
  dates,
  setDates,
  isWarning,
  setIsWarning,
  form,
  maxAllowedDaysForSupplierRequest,
  cutoffTime,
}) => {
  const { t } = useTranslation()
  const { state: user } = useUser()
  const queryClient = useQueryClient()
  const deliveryNoteMinLength = DeliveryNoteUtils.DEFAULT_MIN_LENGTH
  const deliveryNoteMaxLength = DeliveryNoteUtils.DEFAULT_MAX_LENGTH
  const deliveryNoteValidationMessage = t('message_delivery_note_number', {
    min: deliveryNoteMinLength,
    max: deliveryNoteMaxLength,
  })
  const documentNumberId = useWatch('documentNumberId', form)
  const materialId = useWatch('materialId', form)
  const typedDeliveryNote = useWatch('number', form)
  const { equipments } = useEquipment()
  const { suppliers } = useSuppliers()

  const [plantId, setPlantId] = useState<string>()
  const [isCalendarVisible, setIsCalendarVisible] = useState<boolean>(false)
  const [isTripCfr, setIsTripCfr] = useState<boolean>(false)
  const [initialDate, setInitialDate] = useState<Date[]>()
  const [availableQuantity, setAvailableQuantity] = useState<string>()
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier>()
  const [selectedPurchaseOrder, setSelectedPurchaseOrder] = useState<SapItemSupplier>()
  const [enableDocument, setEnableDocument] = useState<boolean>(false)

  const initialValues = useMemo(
    () => ({
      count: 1,
      supplierId: ticket.supplier.id ?? undefined,
      depotId: ticket.origin.id ?? undefined,
      materialId: ticket.material.idSap ?? undefined,
      equipmentId: ticket.equipment ? ticket.equipment.id : undefined,
      destinationId: ticket.destination ? ticket.destination.id : undefined,
      carrierId: ticket.carrier ? ticket.carrier.id : undefined,
      comments: ticket.comments ?? undefined,
      number: ticket.deliveryNote,
      approved: ticket.status.name !== CREATED,
      documentNumberId: ticket.documentNumberId,
      withdrawalTime: ticket.withdrawalDate,
      declinedLoad: !!ticket.declinedLoadAt,
      declinedLoadType: ticket.declinedLoadType,
      ticketSap: ticket.ticketSap,
      tripType: ticket.tripType,
    }),
    [ticket],
  )

  const isCommercial = user?.roles?.[0]?.name === RoleEnum.COMMERCIAL

  const commercialSuppliers = useMemo(
    () =>
      isCommercial ? (user?.userDetail?.commercialSupplier?.suppliers ?? []).filter(Boolean) : [],
    [isCommercial, user?.userDetail?.commercialSupplier?.suppliers],
  )

  const effectiveSupplier = useMemo<Supplier | undefined>(() => {
    if (isCommercial) {
      return commercialSuppliers.find(s => s.id === ticket.supplier.id) ?? ticket.supplier
    }
    return user?.userDetail?.supplier
  }, [isCommercial, commercialSuppliers, ticket.supplier, user?.userDetail?.supplier])

  const { purchaseOrders } = usePurchaseOrdersByMaterial(
    effectiveSupplier?.sapId || selectedSupplier?.sapId,
    plantId || ticket.destination?.plantId,
    ticket.withdrawalDate ? new Date(ticket.withdrawalDate).toISOString() : undefined,
  )
  const { materials, materialsOptionsSapCode } = useMaterials(
    selectedPurchaseOrder?.DocumentNumberID || documentNumberId,
    undefined,
    ticket.withdrawalDate ? new Date(ticket.withdrawalDate).toISOString() : undefined,
  )
  const currentDocumentNumberId = selectedPurchaseOrder?.DocumentNumberID || documentNumberId
  const {
    deliveryNotesOptions,
    isDeliveryNotesLoading,
    isSyncingDeliveryNotes,
    showDeliveryNoteNotFoundWarning,
    syncDeliveryNotes,
  } = useDeliveryNotes(currentDocumentNumberId, materialId, materials, {
    currentDeliveryNote:
      String(currentDocumentNumberId) === String(ticket.documentNumberId ?? '')
        ? ticket.deliveryNote
        : undefined,
    typedDeliveryNote,
  })

  const purchaseOrdersFilteredByPlant = useMemo(() => {
    const byPlant = plantId
      ? purchaseOrders?.filter(({ PlantID }) => PlantID === plantId)
      : purchaseOrders
    return filterPurchaseOrdersByTripType(
      byPlant,
      ticket.tripType,
      documentNumberId || ticket.documentNumberId,
    )
  }, [plantId, purchaseOrders, ticket.tripType, ticket.documentNumberId, documentNumberId])
  const { allDestinationsOptions, destinations } = useDestinations(effectiveSupplier?.sapId)

  const canEditDestination = useMemo(
    () =>
      !!editable ||
      (ticket.tripType === TripTypeEnum.FOB &&
        destinationEditableStatuses.includes(ticket.status.name as StatusTicketEnum)),
    [editable, ticket.status.name, ticket.tripType],
  )

  // La PO solo es editable en estados previos a la entrega. En DELIVERED
  // (status_ticket.id = 5) queda en solo lectura para Supplier y Comercial.
  const canEditPurchaseOrder = useMemo(
    () =>
      editablePurchaseOrderStatus.includes(ticket.status.name as StatusTicketEnum) &&
      !!enableDocument &&
      !!plantId,
    [ticket.status.name, enableDocument, plantId],
  )

  const declinedLoadValue = useWatch('declinedLoad', form)
  const tripTypeValue = useWatch('tripType', form) as TripTypeEnum

  useEffect(() => {
    setIsTripCfr(tripTypeValue === TripTypeEnum.CFR)
  }, [tripTypeValue])

  // Los campos Remito y Ticket SAP solo deben estar habilitados cuando el estado es CONFIRMED o TRANSIT
  const canEditRemitoAndTicketSap = useMemo(() => {
    const status = ticket.status.name
    // Habilitar solo si el estado es CONFIRMED o TRANSIT
    return status === CONFIRMED || status === TRANSIT
  }, [ticket.status.name])

  const isDeliveryNoteFieldEnabled = canEditRemitoAndTicketSap && !!documentNumberId && !!materialId

  const isRequiredMsg = t('globals.obligatory_field')

  const closeCalendar = () => {
    setIsCalendarVisible(false)
  }

  const resetPurchaseOrders = () => {
    form.resetFields(['depotId', 'materialId', 'destinationId'])
    form.setFieldsValue({ documentNumberId: undefined, destinationId: undefined })
    setPlantId(undefined)
    setSelectedPurchaseOrder(undefined)
    setAvailableQuantity(undefined)
    queryClient.removeQueries([PURCHASES])
  }

  const materialLabel = useMemo(
    () =>
      materialsOptionsSapCode?.find(material => material.value === ticket.material.idSap)?.label ??
      `${ticket.material.nameShort} ${ticket.material.sapCode.replace(/^0+/, '')}`,
    [materialsOptionsSapCode, ticket.material.idSap],
  )

  const truckInfo = useMemo(
    () =>
      OtherUtils.joinArrayValuesOnText([
        ticket.truck?.licensePlate,
        ticket.truck?.description,
        ticket.truck?.equipmentType?.nameShort,
      ]),
    [ticket],
  )

  const trailerInfo = useMemo(
    () =>
      OtherUtils.joinArrayValuesOnText([
        ticket.trailer?.licensePlate,
        ticket.trailer?.description,
        ticket.trailer?.equipmentType?.nameShort,
      ]),
    [ticket],
  )

  const handleDestinationSelect = useCallback(
    (value: number) => {
      handlePrimaryButtonDisabling(true)
      setAvailableQuantity(undefined)
      setSelectedPurchaseOrder(undefined)
      form.setFieldsValue({ materialId: undefined, documentNumberId: undefined, number: undefined })
      const destinationPlantId =
        destinations && destinations.find(destination => destination.id === value)
      const filteredPlantId = destinationPlantId?.plantId
      setPlantId(filteredPlantId)
    },
    [destinations, form, handlePrimaryButtonDisabling],
  )

  const isDateAfterToday = useCallback(
    () => dates && dates[0].getTime() < dayjs().toDate().getTime(),
    [dates],
  )

  const applyCutoffTime = useCallback(
    (value: string, inputDates?: Date[]) =>
      inputDates?.map(date => CutoffTimeUtils.applyCutoffTimeToDate(date, value)),
    [],
  )

  const calendarFooter = useCallback(
    () => (
      <FlexRow
        style={{
          justifyContent: 'end',
          padding: '0.5rem',
        }}
      >
        <Button type="primary" onClick={closeCalendar}>
          Aceptar
        </Button>
      </FlexRow>
    ),
    [],
  )

  const handleMaterialsOptions = useCallback(
    (value: number | undefined) => {
      form.setFieldsValue({ materialId: value, number: undefined })
      const selectedMaterialAux = materials?.find(material => material.id === value)
      setAvailableQuantity(selectedMaterialAux?.sapItemSupplier?.AvailableQuantity ?? '')
      handlePrimaryButtonDisabling(false)
    },
    [materials],
  )
  const handleTimeChange = () => {
    if (dates?.length && isDateAfterToday()) {
      if (!isTripCfr) {
        handlePrimaryButtonDisabling(true)
      }
    }
  }

  const handleSupplierOptions = useCallback(
    (value: number | undefined) => {
      resetPurchaseOrders()
      if (value) {
        form.resetFields(['depotId', 'materialId', 'destinationId'])
        form.setFieldsValue({
          documentNumberId: undefined,
          destinationId: undefined,
          materialId: undefined,
          number: undefined,
        })
        setPlantId(undefined)
        const supplierAux = suppliers?.find(supplier => supplier.id === value)
        setSelectedSupplier(supplierAux)
        setEnableDocument(!!supplierAux?.sapId)
      }
    },
    [suppliers],
  )

  const handlePurchaseDocumentChange = useCallback(
    (selectedDocumentNumberId?: string) => {
      const order = purchaseOrders?.find(po => po.DocumentNumberID === selectedDocumentNumberId)
      if (!order) {
        form.setFieldsValue({
          documentNumberId: undefined,
          tripType: ticket.tripType,
          number: undefined,
        })
        setSelectedPurchaseOrder(undefined)
        setDates([])
        form.resetFields(['withdrawalDates'])
        return
      }

      setSelectedPurchaseOrder(order)
      setAvailableQuantity(undefined)
      form.setFieldsValue({
        documentNumberId: selectedDocumentNumberId,
        tripType: ticket.tripType,
        materialId: undefined,
        number: undefined,
      })

      handlePrimaryButtonDisabling(true)
    },
    [purchaseOrders, form, handlePrimaryButtonDisabling, setDates, ticket.tripType],
  )

  const clearDates = () => {
    setDates([])
    form.setFieldValue('withdrawalDates', [])
  }

  const handleChangeDate = useCallback(
    (thisDates?: Dayjs[]) => {
      const sorted = thisDates?.map(d => d.toDate()).sort((a, b) => (a < b ? -1 : 1))
      const datesWithCutoff = applyCutoffTime(cutoffTime, sorted)
      setDates(datesWithCutoff)

      if (thisDates) {
        form.setFieldsValue({ withdrawalDates: datesWithCutoff })
      }
      handlePrimaryButtonDisabling(false)
    },
    [tripTypeValue, isTripCfr, cutoffTime, applyCutoffTime],
  )

  const TOOLTIP_TEXTS: Record<TripTypeEnum, string> = {
    [TripTypeEnum.FOB]: t('selected_Incoterm.fob'),
    [TripTypeEnum.CFR]: t('selected_Incoterm.cfr'),
    [TripTypeEnum.TRANSFERENCE]: '',
  }

  const getTooltipText = (tripType?: TripTypeEnum) =>
    TOOLTIP_TEXTS[tripType || TripTypeEnum.TRANSFERENCE] || ''

  const TripTypeDisplay: React.FC<{ tripType?: TripTypeEnum }> = ({ tripType }) => {
    const displayValue = tripType ? t(`trip_type.${tripType}`) : ''
    return (
      <Tooltip title={getTooltipText(tripType)}>
        <span className="ticket-field-value">{displayValue}</span>
      </Tooltip>
    )
  }

  useEffect(() => {
    if (ticket.withdrawalDate) {
      setInitialDate([dayjs(ticket.withdrawalDate).toDate()])
    } else {
      setInitialDate(undefined)
    }
    setDates([dayjs(ticket.withdrawalDate).toDate()])
    handleSupplierOptions(ticket.supplier.id)

    if (ticket.destination) {
      setPlantId(ticket.destination.plantId || undefined)
    }

    const isEditable =
      (ticket.status.name !== DELIVERED &&
        ticket.status.name !== CONFIRMED &&
        (ticket.status.name === CREATED || ticket.tripType === TripTypeEnum.CFR)) ||
      (ticket.tripType === TripTypeEnum.CFR &&
        !forbiddenSupplierEditionStatus.includes(ticket.status.name as StatusTicketEnum))

    const canEditDestinationFob =
      ticket.tripType === TripTypeEnum.FOB &&
      destinationEditableStatuses.includes(ticket.status.name as StatusTicketEnum) &&
      !forbiddenSupplierEditionStatus.includes(ticket.status.name as StatusTicketEnum)

    if (isEditable) {
      setEditable(true)
      handlePrimaryButtonDisabling(false)
      setEnableDocument(!!ticket.supplier?.sapId)
    } else if (canEditDestinationFob) {
      // Field-level Destination unlock: enable save + PO without full form editability
      setEditable(false)
      handlePrimaryButtonDisabling(false)
      setEnableDocument(!!ticket.supplier?.sapId)
    } else {
      setEditable(false)
      handlePrimaryButtonDisabling(true)
      setEnableDocument(false)
    }

    if (ticket.status.name === DELIVERED) {
      setEditable(false)
      setEnableDocument(false)
    }

    form.resetFields()
    form.setFieldsValue(initialValues)
  }, [ticket, initialValues, form])

  const handleDeliveryNoteChange = useCallback(
    (value: string | string[] | undefined) => {
      const normalized = DeliveryNoteUtils.normalizeValue(value)
      if (
        normalized &&
        !DeliveryNoteUtils.isValidDeliveryNote(
          normalized,
          deliveryNoteMinLength,
          deliveryNoteMaxLength,
        )
      ) {
        notification.error({
          message: deliveryNoteValidationMessage,
          key: 'materialError',
        })
      }
      const materialForSelectedDeliveryNote = materials?.filter(material =>
        material.sapItemSupplier?.sapItemSupplierDeliveryNotes?.some(
          item => item.deliveryNote === normalized,
        ),
      )[0]
      materialChange(materialForSelectedDeliveryNote?.idSap)
    },
    [materials, deliveryNoteMinLength, deliveryNoteMaxLength, deliveryNoteValidationMessage],
  )

  const materialChange = useCallback(
    (value: number | undefined) => {
      const newSelectedMaterial = materials?.find(material => material.idSap === value)
      form.setFieldsValue({ material: newSelectedMaterial?.idSap })
    },
    [form, materials],
  )

  useEffect(() => {
    if (initialDate && !dates) {
      setDates(initialDate)
    }
  }, [initialDate])

  useEffect(() => {
    if (!!dates?.length) {
      setIsWarning(false)
      handleTimeChange()
    }

    form.setFieldsValue({
      withdrawalDates: dates,
    })
  }, [dates, cutoffTime])

  useEffect(() => {
    if (selectedPurchaseOrder) {
      form.setFieldsValue({ materialId: undefined, number: undefined })
      form.setFieldsValue({ tripType: ticket.tripType })
    }
  }, [selectedPurchaseOrder, form, ticket.tripType])

  useEffect(() => {
    if (tripTypeValue === TripTypeEnum.CFR) {
      const currentValue = form.getFieldValue('count')
      if (currentValue !== 1) {
        form.setFieldsValue({ count: 1 })
      }

      if (dates && dates.length > 1) {
        const singleDate = [dates[0]]
        setDates(singleDate)
        form.setFieldsValue({
          withdrawalDates: singleDate,
        })
      }
    }
  }, [tripTypeValue, form, dates])

  return (
    <Form form={form} initialValues={initialValues} name="ticket-form" className="ticket-form">
      <FlexCol className="ticket-form__column ticket-data">
        {!!(ticket && !editable && ticket.status.name) && (
          <StatusTicket status={ticket.status.name}>
            {(ticket.status.name === 'CONFIRMED' || ticket.status.name === 'TRANSIT') && (
              <span style={{ marginLeft: 'auto' }}>
                <Tooltip
                  title={t(
                    'notifications.the_trip_will_be_delivered_when_the_sap_ticket_is_validated.',
                  )}
                  style={{ marginLeft: 'auto' }}
                >
                  <QuestionCircleOutlined />
                </Tooltip>
              </span>
            )}
          </StatusTicket>
        )}
        <FlexCol className="form-section">
          {!editable && (
            <StyledRow className="form-detail-title">
              <span>{`${t('Detalle ID')} #${ticket.id}`}</span>
            </StyledRow>
          )}
          {isCommercial && effectiveSupplier && (
            <StyledRow className="new-ticket-form-row">
              <Form.Item
                label={t('suppliers_router.new_ticket_modal.supplier')}
                name="supplierTradeName"
              >
                <span className="ticket-field-value">
                  {effectiveSupplier.tradeName}
                  {effectiveSupplier.disableAt && (
                    <span style={{ color: 'red' }}>{` (${t('globals.generics.disabled')})`}</span>
                  )}
                </span>
              </Form.Item>
            </StyledRow>
          )}
          {/* ORIGEN */}
          <StyledRow className="new-ticket-form-row">
            <Form.Item
              label={t('suppliers_router.new_ticket_modal.origin')}
              rules={[
                {
                  required,
                  message: isRequiredMsg,
                },
              ]}
              name="depotId"
            >
              <Select
                showSearch
                filterOption={handleFilterOptions}
                disabled
                getPopupContainer={(triggerNode: HTMLElement) =>
                  triggerNode.parentNode as HTMLElement
                }
                placeholder={t('globals.placeholder_select')}
                options={(effectiveSupplier?.depots ?? []).map(depot => ({
                  label: depot.tag,
                  value: depot.id,
                }))}
              />
            </Form.Item>
          </StyledRow>
          {/* DESTINO */}
          <StyledRow className="new-ticket-form-row">
            <Form.Item
              label={t('suppliers_router.new_ticket_modal.destination')}
              rules={[
                {
                  required,
                  message: isRequiredMsg,
                },
              ]}
              name="destinationId"
            >
              <Select
                showSearch
                filterOption={handleFilterOptions}
                allowClear
                disabled={!canEditDestination}
                getPopupContainer={(triggerNode: HTMLElement) =>
                  triggerNode.parentNode as HTMLElement
                }
                placeholder={t('globals.placeholder_select')}
                options={
                  effectiveSupplier?.sapId
                    ? mapDestinationsToCombo(destinations)
                    : allDestinationsOptions
                }
                onChange={handleDestinationSelect}
              />
            </Form.Item>
          </StyledRow>
          {/* ORDEN DE COMPRA */}
          <StyledRow className="new-ticket-form-row">
            <Form.Item
              label={t('suppliers_router.new_ticket_modal.purchase_order')}
              name="documentNumberId"
              rules={[
                {
                  required: enableDocument,
                  message: isRequiredMsg,
                },
              ]}
            >
              <Select
                showSearch
                filterOption={handleFilterOptions}
                disabled={!canEditPurchaseOrder}
                placeholder={!!selectedSupplier?.sapId && t('globals.placeholder_select')}
                getPopupContainer={(triggerNode: HTMLElement) =>
                  triggerNode.parentNode as HTMLElement
                }
                options={purchaseOrdersFilteredByPlant?.map(
                  ({ DocumentNumberID, ProposedDeliveryDate, Inco1, Inco2 }) => ({
                    label: `${DocumentNumberID} - ${OtherUtils.capitalize(
                      dayjs(ProposedDeliveryDate).format('MMM-YY'),
                    )} - ${Inco1}/${Inco2}`,
                    value: DocumentNumberID,
                  }),
                )}
                onChange={handlePurchaseDocumentChange}
              />
            </Form.Item>
          </StyledRow>
          {/* TIPO VIAJE */}
          <StyledRow className="new-ticket-form-row">
            <Form.Item label="Incoterm" name="tripType">
              <div>
                <TripTypeDisplay tripType={tripTypeValue} />
              </div>
            </Form.Item>
          </StyledRow>
          {/* MATERIAL */}
          <StyledRow className="new-ticket-form-row">
            <Form.Item
              label={t('suppliers_router.new_ticket_modal.material')}
              name="materialId"
              rules={[{ required: true, message: isRequiredMsg }]}
            >
              {ticket.status?.name === 'DELIVERED' ? (
                <MaterialLabelContainer>
                  <span>{materialLabel}</span>
                  {selectedPurchaseOrder?.DocumentNumberID !== undefined && (
                    <div>
                      {!!availableQuantity &&
                        `${t(
                          'suppliers_router.new_ticket_modal.available',
                        )} ${availableQuantity} tn`}
                      {` (${t('suppliers_router.new_ticket_modal.available')} ${
                        selectedPurchaseOrder?.AvailableQuantity ?? ''
                      } tn)`}
                    </div>
                  )}
                </MaterialLabelContainer>
              ) : (
                <Select
                  showSearch
                  onChange={handleMaterialsOptions}
                  filterOption={handleFilterOptions}
                  getPopupContainer={(triggerNode: HTMLElement) =>
                    triggerNode.parentNode as HTMLElement
                  }
                  placeholder={t('globals.placeholder_select')}
                  options={materialsOptionsSapCode}
                />
              )}
            </Form.Item>
          </StyledRow>
          {/* EQUIPO */}
          <StyledRow className="new-ticket-form-row">
            <Form.Item
              label={t('suppliers_router.new_ticket_modal.equipment')}
              name="equipmentId"
              rules={[{ required: true, message: isRequiredMsg }]}
            >
              <Select
                showSearch
                filterOption={handleFilterOptions}
                getPopupContainer={(triggerNode: HTMLElement) =>
                  triggerNode.parentNode as HTMLElement
                }
                placeholder={t('globals.placeholder_select')}
                options={equipments?.map(equipment => ({
                  label: equipment.nameShort,
                  value: equipment.id,
                }))}
                // onChange={handleSelectEquipment}
                onChange={(value?: number) => {
                  handlePrimaryButtonDisabling(!value)
                }}
                disabled={!editable}
              />
            </Form.Item>
          </StyledRow>
          {/* FECHA */}
          <StyledRow className="new-ticket-form-row">
            <Form.Item
              label={t('suppliers_router.new_ticket_modal.date')}
              name="withdrawalDate"
              initialValue={ticket.withdrawalDate ? dayjs(ticket.withdrawalDate) : undefined}
              rules={[
                {
                  required: true,
                  message: isRequiredMsg,
                },
              ]}
              validateTrigger="onSubmit"
            >
              {ticket.status?.name === 'DELIVERED' ? (
                <DatePicker
                  className="ticket-form__date-picker-viewer"
                  disabled
                  showTime={{ format: DATE_KEYS.FORMAT.TIME }}
                  format={DATE_KEYS.FORMAT.DAY_SHORT}
                />
              ) : (
                <DatePicker
                  allowClear
                  open={isCalendarVisible}
                  onClick={() => setIsCalendarVisible(true)}
                  onChange={date => {
                    if (!date) {
                      clearDates()
                      form.setFields([{ name: 'withdrawalDate', errors: [isRequiredMsg] }])
                    } else {
                      handleChangeDate([date])
                      form.setFields([{ name: 'withdrawalDate', errors: [] }])
                    }
                  }}
                  format={DATE_KEYS.FORMAT.DAY_SHORT}
                  disabled={!editable}
                  renderExtraFooter={calendarFooter}
                  minDate={!isTripCfr ? tomorrow : today}
                  maxDate={
                    maxAllowedDaysForSupplierRequest
                      ? dayjs().add(maxAllowedDaysForSupplierRequest, 'day')
                      : undefined
                  }
                  className={classesToString([
                    'ticket-form__date-picker-input',
                    isWarning && 'ticket-form__is-warning',
                  ])}
                />
              )}
            </Form.Item>
          </StyledRow>
          <div className="dates-quantity-container">
            {!!dates?.length &&
              `${t('suppliers_router.new_ticket_modal.dates_quantity')} ${dates.length}`}
          </div>
          {/* HORARIO DE CORTE */}
          {!isTripCfr && (
            <StyledRow className="new-ticket-form-row">
              <Form.Item
                label={`${t('globals.cutoff_time')}:`}
                className="cutoff-time-form-item"
                colon={false}
              >
                <span className={classesToString([isWarning && 'ticket-form__is-warning'])}>
                  {t('globals.cutoff_time_info_short', { time: cutoffTime })}
                </span>
              </Form.Item>
            </StyledRow>
          )}
          {ticket.id && ticket.id > 0 && (
            <StyledRow className="new-ticket-form-row">
              <Form.Item label={t('ticket_modal.created_at')} name="createAt">
                <span className="ticket-field-value">
                  {dayjs(ticket.createAt).format(DATE_KEYS.FORMAT.FULL_TIME)}
                </span>
              </Form.Item>
            </StyledRow>
          )}
          <StyledRow className="new-ticket-form-row">
            <Form.Item label={t('ticket_modal.driver')} name="driver">
              <div>
                {ticket.driver ? (
                  <span className="ticket-field-value">
                    {`${ticket.driver?.stateIdNumber} -
                      ${ticket.driver?.firstName} ${ticket.driver?.lastName}`}
                  </span>
                ) : (
                  <span className="ticket-field-value-error">N/A</span>
                )}
              </div>
            </Form.Item>
          </StyledRow>
          <StyledRow className="new-ticket-form-row">
            <Form.Item label={t('ticket_modal.truck')} name="truck">
              <div>
                {ticket.truck ? (
                  <span title={truckInfo} className="ticket-field-value">
                    {trimText(truckInfo, 50)}
                  </span>
                ) : (
                  <span className="ticket-field-value-error">N/A</span>
                )}
              </div>
            </Form.Item>
          </StyledRow>
          <StyledRow className="new-ticket-form-row">
            <Form.Item label={t('ticket_modal.trailer')} name="trailer">
              <div>
                {ticket.trailer && trailerInfo ? (
                  <span title={trailerInfo} className="ticket-field-value">
                    {trimText(trailerInfo, 50)}
                  </span>
                ) : (
                  <span className="ticket-field-value-error">N/A</span>
                )}
              </div>
            </Form.Item>
          </StyledRow>
          {declinedLoadEditionStatus.includes(ticket.status.name as StatusTicketEnum) ? (
            <StyledRow
              className="new-ticket-form-row"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
              }}
            >
              <Form.Item label={t('ticket_modal.load_rejected')} name="declinedLoad">
                {ticket.declinedLoadAt ? (
                  <span className="ticket-field-value">
                    {dayjs(ticket.declinedLoadAt).format(DATE_KEYS.FORMAT.FULL_TIME)}
                  </span>
                ) : (
                  <span className="ticket-field-value-error">N/A</span>
                )}
              </Form.Item>
            </StyledRow>
          ) : null}
          {declinedLoadValue &&
          declinedLoadEditionStatus.includes(ticket.status.name as StatusTicketEnum) ? (
            <StyledRow className="new-ticket-form-row">
              <Form.Item
                rules={[
                  {
                    required: true,
                    message: isRequiredMsg,
                  },
                ]}
                label={t('ticket_modal.reason_for_rejection')}
                name="declinedLoadType"
              >
                {ticket.declinedLoadType ? (
                  <span className="ticket-field-value">
                    {t(`ticket_modal.${ticket.declinedLoadType.toLowerCase()}`)}
                  </span>
                ) : (
                  <span className="ticket-field-value-error">N/A</span>
                )}
              </Form.Item>
            </StyledRow>
          ) : null}
          {/* REMITO */}
          {isTripCfr && (
            <>
              <StyledRow className="carrier-form-row">
                <FlexRow style={{ alignItems: 'start', gap: '8px', flex: 1 }}>
                  <Form.Item
                    label={
                      <DeliveryNoteLabel showNotFoundWarning={showDeliveryNoteNotFoundWarning}>
                        {t('suppliers_router.new_ticket_modal.delivery_note')}
                      </DeliveryNoteLabel>
                    }
                    name="number"
                    rules={[
                      {
                        validator: (_, value) => {
                          const normalized = DeliveryNoteUtils.normalizeValue(value)
                          if (!normalized) {
                            return Promise.reject(new Error(isRequiredMsg))
                          }
                          if (
                            !DeliveryNoteUtils.isValidDeliveryNote(
                              normalized,
                              deliveryNoteMinLength,
                              deliveryNoteMaxLength,
                            )
                          ) {
                            return Promise.reject(new Error(deliveryNoteValidationMessage))
                          }
                          return Promise.resolve()
                        },
                      },
                    ]}
                  >
                    {!isTripCfr && editable ? (
                      <Select
                        allowClear
                        showSearch
                        getPopupContainer={triggerNode => triggerNode.parentNode}
                        placeholder={t('globals.placeholder_select')}
                        options={deliveryNotesOptions}
                        loading={isDeliveryNotesLoading || isSyncingDeliveryNotes}
                        notFoundContent={t('suppliers_router.new_ticket_modal.no_delivery_notes')}
                        onChange={handleDeliveryNoteChange}
                        disabled={!isDeliveryNoteFieldEnabled}
                      />
                    ) : (
                      <Select
                        allowClear
                        showSearch
                        getPopupContainer={triggerNode => triggerNode.parentNode}
                        placeholder={t('globals.placeholder_select')}
                        options={deliveryNotesOptions}
                        loading={isDeliveryNotesLoading || isSyncingDeliveryNotes}
                        notFoundContent={t('suppliers_router.new_ticket_modal.no_delivery_notes')}
                        onChange={value => {
                          handlePrimaryButtonDisabling(false)
                          handleDeliveryNoteChange(value)
                          const input = document.activeElement as HTMLElement
                          if (input) input.blur()
                        }}
                        disabled={!isDeliveryNoteFieldEnabled}
                        {...(currentDocumentNumberId ? {} : { mode: 'tags' as const, maxCount: 1 })}
                      />
                    )}
                  </Form.Item>
                  <Tooltip title={t('suppliers_router.new_ticket_modal.refresh_delivery_notes')}>
                    <Button
                      type="primary"
                      icon={<SyncOutlined spin={isSyncingDeliveryNotes} />}
                      onClick={syncDeliveryNotes}
                      disabled={!isDeliveryNoteFieldEnabled || isSyncingDeliveryNotes}
                      loading={isSyncingDeliveryNotes}
                      className="contract-search-button"
                    />
                  </Tooltip>
                </FlexRow>
              </StyledRow>
              {/* TICKET SAP */}
              <StyledRow className="carrier-form-row ticket-sap-row">
                <Form.Item
                  label={t('suppliers_router.new_ticket_modal.ticketSap')}
                  name="ticketSap"
                  hasFeedback
                  rules={[
                    {
                      required: false,
                      message: isRequiredMsg,
                    },
                    {
                      pattern: /^\d+$/,
                      message: t('message_ticket_sap_number'),
                    },
                  ]}
                >
                  <StyledInput
                    allowClear
                    disabled={!canEditRemitoAndTicketSap}
                    onChange={({ target }) => {
                      const value = target.value
                      if (isTripCfr) {
                        handlePrimaryButtonDisabling(false)
                        return
                      }
                      handlePrimaryButtonDisabling(value.length > 0 && !/^\d+$/.test(value))
                    }}
                  />
                </Form.Item>
              </StyledRow>
            </>
          )}
        </FlexCol>
        {editable && (
          <FlexCol className="form-section">
            <StyledRow className="new-ticket-form-row comments">
              <Form.Item label={t('ticket_modal.comments')} name="comments">
                <TextArea maxLength={1200} rows={3} allowClear />
              </Form.Item>
            </StyledRow>
          </FlexCol>
        )}
        {!!ticket && (
          <FlexCol className="form-section flex-1">
            {!editable ? <TicketFilesVariantDetail /> : <TicketFilesVariantForm />}
          </FlexCol>
        )}
        <FlexCol className="form-section form-comments flex-1">
          <StyledRow className="section-header">
            <Col
              className={editable ? 'form-comments__editable' : 'form-comments__non-editable'}
              span={!editable ? 8 : undefined}
            >
              <p className={classesToString(['form-comments__p', !editable && 'bold'])}>
                {t('ticket_modal.comment')}
              </p>
            </Col>
            <Col span={!editable ? 24 : 16}>
              {ticket.observations?.map((observation: Note) =>
                !editable ? (
                  <CommentElement key={observation.id} data={observation} />
                ) : (
                  <NoteElement key={observation.id} data={observation} />
                ),
              )}
              {ticket?.status.name === CANCELED && ticket.deleteBy && ticket.canceledAt && (
                <Col span={24} style={{ marginTop: '1rem' }}>
                  <Form.Item label={t('ticket_modal.deleted_by')}>
                    <span>
                      {ticket.deleteBy.userDetail?.name} {ticket.deleteBy.userDetail?.surname} (
                      {ticket.deleteBy.roles?.[0]?.name})
                    </span>
                  </Form.Item>
                  <Form.Item label={t('ticket_modal.cancellation_date')}>
                    <span>{dayjs(ticket.canceledAt).format(DATE_KEYS.FORMAT.FULL_TIME)}</span>
                  </Form.Item>
                </Col>
              )}
            </Col>
          </StyledRow>
        </FlexCol>
      </FlexCol>
    </Form>
  )
}

export default UpdateTicketFormSupplier
